import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { FaArrowRight, FaClock, FaBook } from "react-icons/fa";
import {
  GlobalContainer,
  H1,
  H2,
  Paragraph,
  LegalWrapper,
  Logo,
  CTABanner,
  CTABannerButton,
} from "../../../styles/AppStyles";
import { Helmet } from "react-helmet-async";
import { LINKS, href } from "../../../config/links";
import { getLogo } from "../../../utils/helpers";

const GuideGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 2.5rem 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const GuideCard = styled(Link)`
  display: flex;
  flex-direction: column;
  padding: 1.75rem;
  background-color: ${({ theme }) => theme.background};
  border: 2px solid ${({ theme }) => theme.iconColor};
  border-radius: 12px;
  text-decoration: none;
  color: ${({ theme }) => theme.text};
  transition: all 0.3s ease;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15);
    border-color: ${({ theme }) => theme.linkColor};
  }

  h3 {
    margin-top: 1rem;
    margin-bottom: 0.75rem;
    color: ${({ theme }) => theme.text};
    font-size: 1.4rem;
    line-height: 1.4;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  }

  .description {
    flex: 1;
    color: ${({ theme }) => theme.iconColor};
    font-size: 0.95rem;
    line-height: 1.6;
    margin-bottom: 1rem;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  }

  .meta {
    display: flex;
    gap: 1.5rem;
    font-size: 0.85rem;
    color: ${({ theme }) => theme.iconColor};
    padding-top: 1rem;
    border-top: 1px solid ${({ theme }) => theme.iconColor}40;

    .meta-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      svg {
        width: 16px;
        height: 16px;
      }
    }
  }

  .cta {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 1rem;
    color: ${({ theme }) => theme.linkColor};
    font-weight: 600;
    font-size: 0.95rem;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;

    svg {
      width: 16px;
      height: 16px;
      transition: transform 0.2s;
    }

    &:hover svg {
      transform: translateX(4px);
    }
  }
`;

const ColorBadge = styled.span`
  display: inline-block;
  padding: 0.5rem 0.85rem;
  background-color: ${({ $color }) => `${$color}20`};
  color: ${({ $color }) => $color};
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const IntroSection = styled.div`
  background-color: ${({ theme }) =>
    theme.isdarkmode ? "rgba(99, 102, 241, 0.08)" : "rgba(99, 102, 241, 0.05)"};
  border-radius: 12px;
  padding: 2rem;
  margin: 2rem 0;
  border-left: 5px solid ${({ theme }) => theme.linkColor};
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;

  p {
    margin: 0.75rem 0;
    line-height: 1.6;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  }

  strong {
    color: ${({ theme }) => theme.linkColor};
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  }
`;

const QuickGuidesIndex = ({ istouch, isdarkmode }) => {
  const canonicalUrl = href(LINKS.QUICK_GUIDES.INDEX);
  const logoTop = getLogo(isdarkmode);

  const guides = [
    {
      id: "phishing",
      title: "What to Do If You Suspect Phishing",
      description:
        "Learn to recognize phishing emails and attacks. Immediate action steps to take and how to report threats to protect your organization.",
      path: LINKS.QUICK_GUIDES.PHISHING_DETECTION,
      difficulty: "Essential",
      readTime: "5 min",
      color: "#ef4444",
    },
    {
      id: "incident",
      title: "Quick Incident Response Checklist",
      description:
        "Step-by-step checklist for the first hours of a cybersecurity incident. Immediate actions, containment, communication, and recovery.",
      path: LINKS.QUICK_GUIDES.INCIDENT_RESPONSE,
      difficulty: "Critical",
      readTime: "8 min",
      color: "#dc2626",
    },
    {
      id: "password",
      title: "Password Best Practices for SMBs",
      description:
        "Create strong passwords, use a password manager, enable MFA, and implement password policies. Practical guidance for every employee.",
      path: LINKS.QUICK_GUIDES.PASSWORD_SECURITY,
      difficulty: "Essential",
      readTime: "6 min",
      color: "#10b981",
    },
    {
      id: "social",
      title: "Social Engineering Red Flags",
      description:
        "Recognize social engineering tactics, verify requests independently, and report threats. Common scenarios and how to respond.",
      path: LINKS.QUICK_GUIDES.SOCIAL_ENGINEERING,
      difficulty: "Essential",
      readTime: "7 min",
      color: "#f59e0b",
    },
    {
      id: "ransomware",
      title: "Help! My Files Are Locked: Quick Ransomware Response",
      description:
        "Immediate actions to take if your systems are encrypted. Containment, recovery, and what NOT to do.",
      path: LINKS.QUICK_GUIDES.RANSOMWARE_RESPONSE,
      difficulty: "Critical",
      readTime: "8 min",
      color: "#dc2626",
    },
    {
      id: "email",
      title: "Spotting Spoofed & Compromised Email",
      description:
        "Learn to verify sender identity, spot fake emails, and recognize account compromise signs. Safe email practices.",
      path: LINKS.QUICK_GUIDES.EMAIL_SECURITY,
      difficulty: "Essential",
      readTime: "6 min",
      color: "#ef4444",
    },
    {
      id: "breach",
      title: "Did Our Data Get Stolen? Quick Breach Response",
      description:
        "Identify a data breach, take immediate action, notify stakeholders, and understand compliance obligations.",
      path: LINKS.QUICK_GUIDES.DATA_BREACH_RESPONSE,
      difficulty: "Critical",
      readTime: "8 min",
      color: "#dc2626",
    },
    {
      id: "remote",
      title: "Secure Your Home Office & Remote Work",
      description:
        "WiFi security, VPN setup, device hardening, and secure video conferencing. Keep work data safe outside the office.",
      path: LINKS.QUICK_GUIDES.REMOTE_WORK_SECURITY,
      difficulty: "Essential",
      readTime: "7 min",
      color: "#3b82f6",
    },
    {
      id: "device",
      title: "Harden Your Computer & Mobile Device",
      description:
        "OS updates, screen locks, encryption, and backup strategy. Platform-specific setup for Windows, Mac, iOS, and Android.",
      path: LINKS.QUICK_GUIDES.DEVICE_SECURITY,
      difficulty: "Essential",
      readTime: "7 min",
      color: "#10b981",
    },
    {
      id: "vendor",
      title: "Manage Third-Party & Vendor Access Safely",
      description:
        "Vendor assessment, access controls, monitoring, and offboarding. Reduce risk from contractors and service providers.",
      path: LINKS.QUICK_GUIDES.VENDOR_RISK,
      difficulty: "Important",
      readTime: "6 min",
      color: "#8b5cf6",
    },
    {
      id: "malware",
      title: "Is My Device Infected? Malware Detection & Removal",
      description:
        "Recognize malware symptoms, isolate infected systems, and when to call for professional help. Safe removal practices.",
      path: LINKS.QUICK_GUIDES.MALWARE_DETECTION,
      difficulty: "Important",
      readTime: "6 min",
      color: "#ef4444",
    },
    {
      id: "physical",
      title: "Control Who Enters Your Facility",
      description:
        "Badge systems, escort policies, access control, and visitor management. Prevent unauthorized physical access.",
      path: LINKS.QUICK_GUIDES.PHYSICAL_SECURITY,
      difficulty: "Important",
      readTime: "5 min",
      color: "#6b7280",
    },
    {
      id: "payment",
      title: "Stop Payment Fraud Before It Happens",
      description:
        "Recognize payment fraud attempts and business email compromise targeting finance teams. Verification steps and approval processes to prevent losses.",
      path: LINKS.QUICK_GUIDES.PAYMENT_FRAUD,
      difficulty: "Critical",
      readTime: "8 min",
      color: "#dc2626",
    },
  ];

  return (
    <GlobalContainer $istouch={istouch} style={{ marginBottom: ".8rem" }}>
      <Helmet>
        <title>Quick Reference Guides | CSFSafe</title>
        <meta
          name="description"
          content="Quick reference guides for cybersecurity essentials. Learn phishing detection, incident response, password security, and social engineering tactics."
        />
        <meta name="robots" content="index,follow" />
        {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="Quick Reference Guides | CSFSafe"
        />
        <meta
          property="og:description"
          content="Quick reference guides for critical cybersecurity topics. Phishing, incident response, passwords, social engineering."
        />
        {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
        <meta property="og:site_name" content="CSFSafe" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary" />
        <meta
          name="twitter:title"
          content="Quick Reference Guides | CSFSafe"
        />
        <meta
          name="twitter:description"
          content="Quick reference guides for critical cybersecurity topics."
        />
      </Helmet>

      {istouch ? (
        <Logo style={{ position: "relative", zIndex: 0, margin: "0" }}>
          <img
            src={logoTop}
            alt="CSFSafe Logo"
            width={180}
            height={60}
            decoding="async"
            style={{
              maxHeight: "60px",
              minHeight: "40px",
              objectFit: "contain",
              margin: "1rem",
              display: "block",
            }}
          />
        </Logo>
      ) : (
        <H1 as="div">Cybersecurity Learning Center</H1>
      )}

      <LegalWrapper>
        <H1 id="quick-guides-header">Quick Reference Guides</H1>

        <IntroSection>
          <Paragraph>
            <strong>Get critical cybersecurity information fast.</strong> These
            quick reference guides are designed for busy SMB employees and
            leaders. Each guide covers one essential topic with actionable steps,
            real-world scenarios, and clear next steps.
          </Paragraph>
          <Paragraph>
            Print them, share them with your team, and keep them accessible when
            you need immediate guidance.
          </Paragraph>
        </IntroSection>

        <H2>12 Quick Reference Guides</H2>

        <GuideGrid>
          {guides.map((guide) => (
            <GuideCard
              key={guide.id}
              to={guide.path}
              $color={guide.color}
              aria-label={`Read: ${guide.title}`}
            >
              <h3>{guide.title}</h3>
              <p className="description">{guide.description}</p>

              <div className="meta">
                <div className="meta-item">
                  <FaClock />
                  <span>{guide.readTime}</span>
                </div>
                <div className="meta-item">
                  <FaBook />
                  <span>Printable</span>
                </div>
              </div>

              <div className="cta">
                Read Guide <FaArrowRight />
              </div>
            </GuideCard>
          ))}
        </GuideGrid>

        <H2 style={{ marginTop: "3rem" }}>Why These Guides Matter</H2>

        <Paragraph>
          <strong>Phishing Detection:</strong> Phishing is the #1 entry point for
          attackers. Knowing how to recognize and report suspicious emails
          protects everyone.
        </Paragraph>

        <Paragraph>
          <strong>Incident Response:</strong> When an attack happens, the first
          few hours are critical. This guide helps you take the right immediate
          actions to minimize damage.
        </Paragraph>

        <Paragraph>
          <strong>Password Security:</strong> Weak or reused passwords put your
          entire organization at risk. Learn how to create strong passwords and
          protect them.
        </Paragraph>

        <Paragraph>
          <strong>Social Engineering:</strong> Attackers manipulate people more
          than they attack technology. Recognize common tactics and verify before
          you act.
        </Paragraph>

        <Paragraph>
          <strong>Ransomware Response:</strong> Ransomware attacks are increasing
          and devastating. Knowing immediate containment steps and recovery
          procedures can save your organization from permanent data loss.
        </Paragraph>

        <Paragraph>
          <strong>Email Security:</strong> Beyond phishing, attackers spoof email
          addresses and compromise accounts. Learn to verify sender identity and
          spot account takeover signs before damage spreads.
        </Paragraph>

        <Paragraph>
          <strong>Data Breach Response:</strong> A breach isn't just a technical
          problem—it has legal and compliance implications. This guide walks you
          through identification, containment, notification, and documentation.
        </Paragraph>

        <Paragraph>
          <strong>Remote Work Security:</strong> Hybrid work is here to stay. This
          guide ensures employees protect company data whether they're working
          from home, coffee shops, or the office.
        </Paragraph>

        <Paragraph>
          <strong>Device Security:</strong> The weakest device in your organization
          becomes an entry point for attackers. Hardening computers and phones with
          updates, encryption, and backups is non-negotiable.
        </Paragraph>

        <Paragraph>
          <strong>Vendor Risk Management:</strong> Third-party vendors and
          contractors access your systems and data. Managing that access and knowing
          what to expect in a breach protects you from supply chain attacks.
        </Paragraph>

        <Paragraph>
          <strong>Malware Detection:</strong> Malware isn't always obvious. This
          guide teaches employees to recognize symptoms and take safe isolation
          steps before professional help arrives.
        </Paragraph>

        <Paragraph>
          <strong>Physical Security:</strong> Not all attacks are digital. Badge
          systems, escort policies, and access control keep unauthorized people
          away from sensitive data and systems.
        </Paragraph>

        <H2>How to Use These Guides</H2>

        <ul>
          <li>
            <strong>Individual Learning:</strong> Read a guide to understand a
            topic and share with others
          </li>
          <li>
            <strong>Team Training:</strong> Print guides and use them in security
            awareness sessions
          </li>
          <li>
            <strong>Quick Reference:</strong> Keep guides accessible in your
            helpdesk or operations area
          </li>
          <li>
            <strong>Incident Response:</strong> Have a copy of the Incident
            Response Checklist available in a crisis
          </li>
          <li>
            <strong>New Hire Onboarding:</strong> Include guides in your
            cybersecurity orientation
          </li>
        </ul>

        <H2 style={{ marginTop: "2.5rem" }}>More Learning Resources</H2>

        <Paragraph>
          These quick guides are just the start. Explore deeper learning in the
          Cybersecurity Learning Center:
        </Paragraph>

        <ul>
          <li>
            <Link to={LINKS.LEARNING_CENTER}>Learning Center</Link> - Full
            courses on cybersecurity topics
          </li>
          <li>
            <Link to={LINKS.THREAT_TYPES}>Threat Types</Link> - Understand
            specific cyber threats
          </li>
          <li>
            <Link to={LINKS.TRAINING_VIDEOS}>Training Videos</Link> - Watch
            expert guidance
          </li>
          <li>
            <Link to={LINKS.CYBERSECURITY_FRAMEWORK.POLICIES_AND_PROCEDURES}>
              Policies &amp; Procedures
            </Link>
            - Build your security program
          </li>
        </ul>

        <CTABanner style={{ marginTop: "2.5rem" }}>
          <span>Ready to build your cybersecurity playbook?</span>
          <CTABannerButton
            href={href(LINKS.ACCOUNT.SUBSCRIBE)}
            aria-label="Get the CSFSafe Cybersecurity Playbook desktop app"
          >
            Get the App — $499 one-time purchase
          </CTABannerButton>
        </CTABanner>
      </LegalWrapper>
    </GlobalContainer>
  );
};

QuickGuidesIndex.propTypes = {
  istouch: PropTypes.bool.isRequired,
  isdarkmode: PropTypes.bool.isRequired,
};

export default QuickGuidesIndex;
